import React, { JSX, useEffect, useMemo, useState } from 'react';

import { invitationClient } from 'clients';
import NewTabLink from 'components/atoms/NewTabLink';
import rollbarLogger from 'config/rollbar';
import { useMyUserHasInternalPermission } from 'store/modules/myUser/hooks';
import { ProtectedResource, ResourceAction } from 'types/action.enum';
import Enterprise from 'types/models/enterprise';
import Invitation from 'types/models/invitation';
import { APIResponseParsed } from 'types/response';

/**
 * Displays a notice when an enterprise is waiting for an owner to accept their invitation
 * @param enterpriseId
 * @param ownerId - userId of the current enterprise owner
 * @param isDisabledOrDuplicate - enterprise status is either disabled or duplicate
 * @returns JSX element to display or undefined if no notice is needed
 */
export const usePendingOwnerNotice = (
  enterpriseId: Enterprise['id'] | undefined,
  ownerId: Enterprise['ownerId'] | undefined,
  isDisabledOrDuplicate: boolean
): JSX.Element | undefined => {
  const [pendingOwnerEmail, setPendingOwnerEmail] = useState('');

  const canInviteMember = useMyUserHasInternalPermission({
    resource: ProtectedResource.INVITATION,
    action: ResourceAction.ADD
  });
  const canViewInvitations = useMyUserHasInternalPermission({
    resource: ProtectedResource.INVITATION,
    action: ResourceAction.READ
  });

  /** Enterprise exists but the owner is not on myFarmbot platform */
  const isEnterprisePendingOwner = enterpriseId && !isDisabledOrDuplicate && !ownerId;

  useEffect(() => {
    const fetchPendingOwnerEmail = async () => {
      const response: APIResponseParsed<Invitation[]> = await invitationClient.fetchInvitations({ enterpriseId });
      if (response.error) {
        rollbarLogger.error('[usePendingOwnerNotice] Error fetching invitations', {
          enterpriseId,
          error: response.error
        });
        setPendingOwnerEmail('');
      } else {
        const ownerInvitation: Invitation | undefined = response.data.find(inv => inv.isEnterpriseOwner);
        setPendingOwnerEmail(ownerInvitation?.email || '');
      }
    };

    if (isEnterprisePendingOwner) {
      fetchPendingOwnerEmail();
    } else {
      setPendingOwnerEmail('');
    }
  }, [enterpriseId, isEnterprisePendingOwner]);

  // Memoize to avoid creating new JSX elements on every render
  const result = useMemo(() => {
    // No notice needed if the enterprise is not waiting for an owner
    if (!isEnterprisePendingOwner) return undefined;

    // Couldn't find a matching invitation. Invitation has probably expired or been removed.
    if (!pendingOwnerEmail) {
      return canInviteMember
        ? <>Invitation expired or removed. Invite new or select another owner</>
        : <>Invitation expired or removed</>;
    }

    if (!canViewInvitations) return <>Invitation pending acceptance</>;

    const invitationsSearchParams = new URLSearchParams();
    invitationsSearchParams.append('search', pendingOwnerEmail); // More URI-safe than plain string

    const invitationSearch: string = `/back-office/invitations?${invitationsSearchParams.toString()}`;
    return (
      <>
        Invitation <NewTabLink href={invitationSearch} text="pending acceptance" />
      </>
    );
  }, [isEnterprisePendingOwner, pendingOwnerEmail, canViewInvitations, canInviteMember]);

  return result;
};